import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';
import { useUser } from '../../context/UserContext';
import { BACKEND_URL } from '../../Config/Const';

const EncuestaFinalizar = ({ visible, onClose, onSubmit, onOpen }) => {
  const { mail, codUsu } = useUser();
  const [valoracion, setValoracion] = useState(null);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  
  // Estado para controlar responsividad
  const getWindowWidth = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 768;
  };
  
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const isMobile = windowWidth < 768;
  
  // Estado para la altura del documento
  const [documentHeight, setDocumentHeight] = useState('100vh');
  
  // Calcular altura total del documento
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined' && visible) {
      const updateHeight = () => {
        const body = document.body;
        const html = document.documentElement;
        const height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        setDocumentHeight(`${height}px`);
      };
      
      updateHeight();
      setTimeout(updateHeight, 100);
    }
  }, [visible]);
  
  // Actualizar tamaño al montar y al cambiar
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  // Bloquear scroll del body cuando el modal está visible y scroll automático arriba
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (visible) {
        // Guardar posición del scroll antes de bloquear
        const scrollY = window.scrollY;
        
        // Scroll instantáneo hacia arriba (mejor para móviles)
        window.scrollTo(0, 0);
        
        // Bloquear scroll inmediatamente
        document.body.style.position = 'fixed';
        document.body.style.top = '0px';
        document.body.style.left = '0px';
        document.body.style.right = '0px';
        document.body.style.width = '100%';
        document.body.style.height = '100vh';
        document.body.style.overflow = 'hidden';
        
        // Prevenir bounce en iOS y bloquear overscroll
        document.body.style.webkitOverflowScrolling = 'touch';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.overscrollBehavior = 'none';
        
        // Prevenir scroll mediante eventos
        const preventScroll = (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };
        
        const preventTouchMove = (e) => {
          // Permitir scroll dentro del contenido del modal
          const modalContent = e.target.closest('[data-modal-content="true"]');
          if (modalContent) {
            // Permitir scroll dentro del modal
            return true;
          }
          
          // Prevenir scroll en el overlay o body
          if (e.target === document.body || e.target.closest('.modal-overlay')) {
            e.preventDefault();
            return false;
          }
        };
        
        // Agregar listeners para prevenir scroll
        window.addEventListener('scroll', preventScroll, { passive: false });
        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventTouchMove, { passive: false });
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
        
        return () => {
          // Remover event listeners
          window.removeEventListener('scroll', preventScroll);
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('touchmove', preventTouchMove);
          document.removeEventListener('touchmove', preventTouchMove);
          
          // Restaurar estilos
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';
          document.body.style.height = '';
          document.body.style.overflow = '';
          document.body.style.webkitOverflowScrolling = '';
          document.body.style.overscrollBehavior = '';
          document.documentElement.style.overflow = '';
          document.documentElement.style.overscrollBehavior = '';
          
          // Volver a la posición original del scroll
          window.scrollTo(0, scrollY);
        };
      }
    }
  }, [visible]);
  
  // Notificar al padre cuando el modal se muestra para permitir ocultar loaders
  useEffect(() => {
    if (visible && typeof onOpen === 'function') {
      onOpen();
    }
  }, [visible, onOpen]);
  
  // Opciones de valoración para mantenerlas en un solo lugar
  const opcionesValoracion = [
    { id: 'contenta', icon: 'emoticon-happy-outline', texto: 'Excelente', color: '#4CAF50' },
    { id: 'neutral', icon: 'emoticon-neutral-outline', texto: 'Normal', color: '#FFC107' },
    { id: 'triste', icon: 'emoticon-sad-outline', texto: 'No tan buena', color: '#E53935' }
  ];

  // Mapear valoración a número (1, 2 o 3)
  const obtenerNota = (valoracion) => {
    const mapeo = {
      'triste': 1,
      'neutral': 2,
      'contenta': 3
    };
    return mapeo[valoracion] || 2;
  };

  // Estilos dinámicos basados en isMobile
  const getDynamicStyles = () => ({
    modalContainer: {
      ...(Platform.OS === 'web' ? {
        minHeight: documentHeight,
        justifyContent: 'center',
        paddingTop: isMobile ? 20 : 0,
        paddingBottom: isMobile ? 20 : 0,
      } : {
        justifyContent: 'center',
      }),
    },
    modalContent: {
      width: isMobile ? '92%' : '60%',
      maxWidth: isMobile ? '92%' : 520,
      padding: isMobile ? 16 : 20,
      maxHeight: isMobile ? '90vh' : '80vh',
      ...(Platform.OS === 'web' ? {
        display: 'flex',
      } : {}),
      ...(isMobile ? {
        overflowY: 'hidden',
        overflowX: 'hidden',
        marginTop: 10,
      } : {
        overflowY: 'hidden',
      }),
    },
    scrollWrapper: {
    width: '100%',
  },
  scrollContent: {
    width: '100%',
  },
  scrollContentMobile: {
    paddingBottom: 16,
  },
  scrollContentDesktop: {
    paddingBottom: 24,
  },
  titulo: {
      fontSize: isMobile ? 20 : 24,
      marginBottom: isMobile ? 8 : 10,
    },
    subtitulo: {
      fontSize: isMobile ? 14 : 16,
      marginBottom: isMobile ? 20 : 25,
    },
    caritaButton: {
      padding: isMobile ? 8 : 10,
    },
    caritaText: {
      fontSize: isMobile ? 10 : 12,
    },
    commentInput: {
      minHeight: isMobile ? 80 : 100,
      padding: isMobile ? 8 : 10,
    },
    submitButton: {
      paddingVertical: isMobile ? 12 : 15,
      marginTop: isMobile ? 16 : 20,
    },
  });

  // Manejar el envío de la encuesta
  const handleSubmit = async () => {
    if (!valoracion || enviando) return;
    
    setEnviando(true);
    
    try {
      // Preparar los datos para enviar
      const nota = obtenerNota(valoracion);
      const observaciones = comentario.trim() || '';
      
      // Construir query params
      const params = new URLSearchParams();
      if (mail) params.append('mail', mail);
      if (codUsu) params.append('codUsu', codUsu);
      params.append('nota', nota.toString());
      params.append('observaciones', observaciones);
      
      const url = `${BACKEND_URL}/rest/fin/encuesta?${params.toString()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        
        const datosEncuesta = {
          valoracion,
          nota,
          comentario: observaciones,
          fecha: new Date().toISOString()
        };
        
        // Llamar a la función onSubmit para que el componente padre pueda manejar la navegación
        onSubmit(datosEncuesta);
        
        // Recargar la página para actualizar el carrito
        if (Platform.OS === 'web') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } else {
        console.error('❌ Error al enviar encuesta:', response.status);
        // Aún así, permitir continuar
        onSubmit({ valoracion, nota: obtenerNota(valoracion), comentario: observaciones });
        
        // Recargar la página para actualizar el carrito
        if (Platform.OS === 'web') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    } catch (error) {
      console.error('❌ Error al enviar encuesta:', error);
      // Aún así, permitir continuar
      onSubmit({ valoracion, nota: obtenerNota(valoracion), comentario: comentario.trim() });
      
      // Recargar la página para actualizar el carrito
      if (Platform.OS === 'web') {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } finally {
      setEnviando(false);
    }
  };

  const dynamicStyles = getDynamicStyles();

  // Prevenir scroll en el modal container
  const handleTouchMove = (e) => {
    // Solo prevenir si el toque es en el overlay, no en el contenido
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };

  const renderSurveyContent = () => (
    <ScrollView
      style={styles.scrollWrapper}
      contentContainerStyle={[
        styles.scrollContent,
        isMobile ? styles.scrollContentMobile : styles.scrollContentDesktop
      ]}
      showsVerticalScrollIndicator={!isMobile}
    >
      <Text style={[styles.titulo, dynamicStyles.titulo, { color: colores.verde }]}>¡Pago realizado con éxito!</Text>
      <Text style={[styles.titulo, dynamicStyles.titulo]}>¡Gracias por tu compra!</Text>
      <Text style={[styles.subtitulo, dynamicStyles.subtitulo]}>¿Cómo calificarías tu experiencia de compra?</Text>

      <View style={styles.caritasContainer}>
        {opcionesValoracion.map(opcion => (
          <TouchableOpacity 
            key={opcion.id}
            style={[
              styles.caritaButton, 
              dynamicStyles.caritaButton, 
              valoracion === opcion.id && { borderColor: opcion.color, backgroundColor: `${opcion.color}15` }
            ]}
            onPress={() => setValoracion(opcion.id)}
          >
            <MaterialCommunityIcons 
              name={opcion.icon}
              size={isMobile ? 32 : 40} 
              color={valoracion === opcion.id ? opcion.color : colores.grisClaro} 
            />
            <Text style={[styles.caritaText, dynamicStyles.caritaText]}>{opcion.texto}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={styles.commentLabel}>¿Quieres dejarnos un comentario? (Opcional)</Text>
      <TextInput
        style={[styles.commentInput, dynamicStyles.commentInput]}
        multiline={true}
        numberOfLines={isMobile ? 3 : 4}
        placeholder="Escribe tu opinión aquí..."
        value={comentario}
        onChangeText={setComentario}
      />

      <TouchableOpacity 
        style={[styles.submitButton, dynamicStyles.submitButton, (!valoracion || enviando) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!valoracion || enviando}
      >
        {enviando ? (
          <ActivityIndicator color={colores.blanco} />
        ) : (
          <Text style={styles.submitButtonText}>Finalizar</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity style={styles.skipButton} onPress={onClose}>
        <Text style={styles.skipButtonText}>Omitir</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  return (
    Platform.OS === 'web' && !isMobile ? (
      visible && (
        <View 
          style={[styles.modalContainer, dynamicStyles.modalContainer]}
          className="modal-overlay"
          nativeID="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          onTouchMove={handleTouchMove}
        >
          <View 
            style={[styles.modalContent, dynamicStyles.modalContent]}
            {...(Platform.OS === 'web' ? { 'data-modal-content': 'true' } : {})}
            nativeID="modal-content"
          >
            {renderSurveyContent()}
          </View>
        </View>
      )
    ) : (
      <Modal
        visible={visible}
        transparent={true}
        animationType="fade"
        onRequestClose={onClose}
      >
        <View 
          style={[styles.modalContainer, dynamicStyles.modalContainer]}
          className="modal-overlay"
          nativeID="modal-overlay"
        >
          <View 
            style={[styles.modalContent, dynamicStyles.modalContent]}
            {...(Platform.OS === 'web' ? { 'data-modal-content': 'true' } : {})}
            nativeID="modal-content"
          >
            {renderSurveyContent()}
          </View>
        </View>
      </Modal>
    )
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 20,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      minHeight: '100vh',
      zIndex: 999999,
      display: 'flex',
      overflow: 'auto',
    } : {
      flex: 1,
    }),
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: colores.blanco,
    borderRadius: 8,
    padding: 20,
    ...(Platform.OS === 'web'
      ? { 
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          zIndex: 1000000,
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }
    )
  },
  titulo: {
    fontSize: 24,
    fontFamily: fuentes.bold,
    color: colores.rojo,
    textAlign: 'center',
    marginBottom: 10
  },
  subtitulo: {
    fontSize: 16,
    fontFamily: fuentes.regular,
    textAlign: 'center',
    marginBottom: 25
  },
  caritasContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginVertical: 20
  },
  caritaButton: {
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'transparent'
  },
  caritaSelected: {
    borderColor: colores.rojo,
    backgroundColor: '#FFF5F5'
  },
  caritaText: {
    marginTop: 5,
    fontSize: 12,
    fontFamily: fuentes.regular
  },
  commentLabel: {
    fontSize: 14,
    fontFamily: fuentes.semiBold,
    marginVertical: 10
  },
  commentInput: {
    borderWidth: 1,
    borderColor: colores.grisClaro,
    borderRadius: 8,
    padding: 10,
    textAlignVertical: 'top',
    minHeight: 100
  },
  submitButton: {
    backgroundColor: colores.rojo,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20
  },
  submitButtonDisabled: {
    backgroundColor: colores.grisClaro
  },
  submitButtonText: {
    color: colores.blanco,
    fontSize: 16,
    fontFamily: fuentes.bold
  },
  skipButton: {
    padding: 15,
    alignItems: 'center',
    marginTop: 10
  },
  skipButtonText: {
    color: colores.grisClaro,
    fontSize: 14,
    fontFamily: fuentes.regular
  }
});

export default EncuestaFinalizar;
